'use client'

import { useTranslation } from '@/hooks/client/use-translation'
import SvgArrowRight from '@/icons/thin/ArrowRight'
import SvgPhone from '@/icons/thin/Phone'
import { popupEventName } from '@/lib/popups/constants'

import { CaptionCallFeatures } from './captioncall-features'
import { popupClickTracking } from './click-tracking'
import type { PopupTemplateProps } from './template-props'

// The CaptionCall exit-intent modal, ported from the retired hand-built
// component (captioncall-exit-intent-popup). Copy stays in the i18n
// dictionaries (captioncall.* keys); the DB row contributes the CTA url,
// targeting (/captioned-telephones*) and the frequency caps. Renders inside
// ModalFrame (kind='modal'), and varies its lead copy on the trigger reason —
// a mouse-exit gets the "Wait!" framing, scroll/inactivity the engaged one.

const PHONE = '1-888-309-0537'
const PHONE_TEL = '18883090537'
const LOGO_SRC =
  'https://a.storyblok.com/f/45415/2098x459/3d552cdff6/cc-by-sorenson-logo.png/m/832x0/filters:quality(40)'

export const CaptionCallPopup = ({ popup, onCtaClick, reason }: PopupTemplateProps) => {
  const { t } = useTranslation()
  const variant = reason === 'desktop-mouseleave-top' ? 'exit' : 'engaged'
  const eventName = popupEventName(popup.slug)

  const subtitle =
    variant === 'exit'
      ? t('captioncall.subtitle.exit', 'Wait! Phone call captioning at no cost')
      : t('captioncall.subtitle.engaged', 'Phone call captioning at no cost')
  const body =
    variant === 'exit'
      ? t(
          'captioncall.body.exit',
          'Before you go — if you struggle to hear on the phone, CaptionCall provides a captioned phone at no cost through a federally funded program. Read real-time captions of your calls on a large, high-resolution touchscreen.'
        )
      : t(
          'captioncall.body.engaged',
          'If you struggle to hear on the phone, CaptionCall provides a captioned phone at no cost through a federally funded program. Read real-time captions of your calls on a large, high-resolution touchscreen.'
        )

  const phoneTrackingAttrs = {
    ...popupClickTracking(reason),
    'data-event-name': eventName,
    'data-event-position': `${popup.slug}-call`,
    'data-event-origin': popup.slug,
    'data-event-product': 'CaptionCall',
  }

  return (
    <div className="px-6 pt-10 pb-6 md:px-10 md:pt-12 md:pb-10">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-12 md:gap-8">
        <div className="space-y-2 md:hidden">
          <span className="text-lapis block text-xs leading-none font-bold tracking-wide uppercase">{subtitle}</span>
          <div className="text-navy text-3xl font-bold">CaptionCall</div>
        </div>

        <div className="flex flex-col gap-4 md:col-span-5">
          <div className="relative aspect-[3/2] overflow-hidden rounded-xl bg-slate-100 shadow-inner">
            <div className="flex h-full w-full items-center justify-center p-6">
              <img
                src={LOGO_SRC}
                alt="CaptionCall by Sorenson"
                loading="lazy"
                className="h-full w-full object-contain"
              />
            </div>
          </div>

          <a
            href={`tel:${PHONE_TEL}`}
            {...phoneTrackingAttrs}
            className="text-lapis hidden items-center justify-center gap-2 text-[15px] font-semibold hover:underline md:flex"
          >
            <SvgPhone className="h-[15px] w-[15px]" aria-hidden="true" />
            {PHONE}
          </a>
        </div>

        <div className="flex flex-col justify-between md:col-span-7">
          <div className="space-y-4">
            <div className="hidden space-y-2 md:block">
              <span className="text-lapis block text-xs leading-none font-bold tracking-wide uppercase">
                {subtitle}
              </span>
              <div className="text-navy text-3xl font-bold">CaptionCall</div>
            </div>

            <p className="text-navy md:text-md text-base leading-relaxed tracking-tight">{body}</p>

            <CaptionCallFeatures />
          </div>

          <div className="mt-6 flex flex-col gap-3">
            <a
              href={popup.url}
              target="_blank"
              rel="noopener noreferrer nofollow sponsored"
              {...popupClickTracking(reason)}
              data-event-name={eventName}
              data-event-position={`${popup.slug}-button`}
              data-event-origin={popup.slug}
              data-event-product="CaptionCall"
              onClick={onCtaClick}
              className="group/btn bg-navy hover:bg-navy-90 flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg py-3.5 text-sm font-bold text-white shadow-md transition-all"
            >
              {t('captioncall.learnMore', 'Learn More')}
              <SvgArrowRight
                className="h-4 w-4 transition-transform group-hover/btn:translate-x-1"
                aria-hidden="true"
              />
            </a>

            <a
              href={`tel:${PHONE_TEL}`}
              {...phoneTrackingAttrs}
              onClick={onCtaClick}
              className="border-navy text-navy hover:bg-navy/5 flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg border py-3.5 text-sm font-bold transition-all md:hidden"
            >
              <SvgPhone className="h-4 w-4" aria-hidden="true" />
              {t('captioncall.call', 'Call {{phone}}', { phone: PHONE })}
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
